/* eslint-disable no-unused-vars */
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TableBody,
  TableFooter,
  Accordion, AccordionSummary, Typography, AccordionDetails, Grid, Stack,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { AppContext } from '../../../providers/authProvider';
import api from '../../../api/req';
// eslint-disable-next-line import/no-named-as-default
import documents from '../../../const/meta/documents';
import Loader from '../../../components/MaterialUI/loader';
import { dateFormat, decimalFormat } from '../../../api/utils';
import { EditorControls } from '../../BaseEditor';

function Payments({ contractId }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState([]);
  const { auth } = useContext(AppContext);
  const today = new Date();
  const [filters, setFilters] = useState(() => {
    const d = {
      bdate: new Date(today.getFullYear(), 0, 1),
      edate: new Date(today.getFullYear(), 11, 31),
    };
    Object.keys(d).forEach((k) => {
      const dd = d[k];
      dd.setMinutes(-d[k].getTimezoneOffset());
      d[k] = dd.toISOString().slice(0, 10);
    });
    return d;
  });

  useEffect(
    () => {
      const loader = async () => {
        const r = await api.get(`${documents.contracts.backend}${contractId}/get_payments`, auth, filters);
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      };
      setLoading(true);
      setErr(null);
      loader()
        .then((d) => setData(d))
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [auth, contractId, filters],
  );

  const totals = useMemo(
    () => data.reduce((R, row, i) => ({
      begin: i === 0 ? parseFloat(row.begin_rest) : R.begin,
      plan: R.plan + parseFloat(row.plan_period),
      fact: R.fact + parseFloat(row.fact_period),
      end: parseFloat(row.end_rest),
    }), {
      begin: 0, plan: 0, fact: 0, end: 0,
    }),
    [data],
  );

  return (
    <TableContainer component={Paper} sx={{ height: '100%' }}>
      {err && (
        <Alert severity="error">
          {err}
        </Alert>
      )}
      {loading && (
      <Loader text="Завантаження" />
      )}
      <Stack>
        <Accordion>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faAngleDown} />}>
            <Typography variant="subtitle2">Параметри відбору</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item sm={6}>
                <EditorControls.DateInput
                  label="Дата початку"
                  value={filters.bdate}
                  onChange={(e, v) => setFilters((f) => ({ ...f, bdate: v }))}
                />
              </Grid>
              <Grid item sm={6}>
                <EditorControls.DateInput
                  label="Дата закінчення"
                  value={filters.edate}
                  onChange={(e, v) => setFilters((f) => ({ ...f, edate: v }))}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Stack>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="center">#</TableCell>
            <TableCell align="center">Дата</TableCell>
            <TableCell align="center">Залишок на початок</TableCell>
            <TableCell align="center">Нараховано</TableCell>
            <TableCell align="center">Сплачено</TableCell>
            <TableCell align="center">Залишок на кінець</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, i) => (
            <TableRow key={row.date} hover>
              <TableCell align="right">{i + 1}</TableCell>
              <TableCell>{dateFormat(row.date)}</TableCell>
              <TableCell align="right">{decimalFormat(row.begin_rest)}</TableCell>
              <TableCell align="right">{decimalFormat(row.plan_period)}</TableCell>
              <TableCell align="right">{decimalFormat(row.fact_period)}</TableCell>
              <TableCell align="right">{decimalFormat(row.end_rest)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2} align="right">Разом:</TableCell>
            <TableCell align="right">{decimalFormat(totals.begin)}</TableCell>
            <TableCell align="right">{decimalFormat(totals.plan)}</TableCell>
            <TableCell align="right">{decimalFormat(totals.fact)}</TableCell>
            <TableCell align="right">{decimalFormat(totals.end)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

Payments.propTypes = {
  contractId: PropTypes.number.isRequired,
};

export default Payments;
